import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, BookOpen, Target, Lightbulb, Route, Clock, Dumbbell, BarChart3,
  CheckCircle2, Compass, FileText, Play, Clapperboard, Award, UserCheck,
  HeadphonesIcon, Zap, Layers, Monitor,
} from "lucide-react";
import RelatedContentCarousel from "@/components/RelatedContentCarousel";

// Icon whitelist for CMS-provided icon strings. Editors pick from these
// slugs in the block editor. Keeping the map small keeps the schema simple
// and avoids leaking arbitrary icons into content edits.
const ICONS = {
  zap: Zap,
  layers: Layers,
  route: Route,
  monitor: Monitor,
  "book-open": BookOpen,
  target: Target,
  lightbulb: Lightbulb,
  clock: Clock,
  dumbbell: Dumbbell,
  "bar-chart-3": BarChart3,
  compass: Compass,
  clapperboard: Clapperboard,
  award: Award,
  "file-text": FileText,
  "user-check": UserCheck,
  headphones: HeadphonesIcon,
};

function Icon({ name, className }) {
  const Comp = ICONS[name] || Zap;
  return <Comp className={className} />;
}

// Verbatim defaults from the 8/4/2026 hardcoded version. The CMS block seed
// (backend/seed_data/content_pages.py → ACADEMY_DATA) mirrors this and the
// pre-commit verbatim guard enforces they stay in sync.
export const ACADEMY_DEFAULT_DATA = {
  hero: {
    eyebrow: "Antera Academy",
    title_lead: "Master Antera.",
    title_accent: "In days, not months.",
    tagline: "Your team's home for short, focused modules, curated role-based paths, and hands-on practice - so every user becomes confident, fast.",
    primary_label: "Start Learning",
    primary_href: "/contact",
    secondary_label: "See The Method",
    secondary_href: "#method",
    stats: [
      { value: "50x", label: "Faster Onboarding", icon: "zap" },
      { value: "100+", label: "Focused Modules", icon: "layers" },
      { value: "5", label: "Role-Based Paths", icon: "route" },
      { value: "24/7", label: "Always Available", icon: "monitor" },
    ],
  },
  video: {
    eyebrow: "See it in action",
    heading: "Welcome to Antera Academy",
    src: "https://s3.us-east-1.amazonaws.com/images.anterasoftware.com/Uploads/lms/AnteraAcademy-IntroVideo.mp4",
    poster: "/posters/academy-intro.jpg",
  },
  goals: {
    eyebrow: "Our goal is simple",
    heading: "Learning that actually sticks.",
    items: [
      { icon: "book-open", title: "Easy", desc: "Learn at your own pace with short, focused modules.", color: "bg-sky-500" },
      { icon: "target", title: "Practical", desc: "Build confidence quickly with step-by-step guidance.", color: "bg-sky-600" },
      { icon: "lightbulb", title: "Useful", desc: "Get the most from your implementation, day one.", color: "bg-sky-700" },
    ],
  },
  paths: {
    eyebrow: "Curated Learning Paths",
    title_prefix: "Built for your",
    title_accent: "exact role.",
    tagline: "We've curated courses based entirely on your specific responsibilities, guiding you through the essentials in the right order - so nothing important gets skipped.",
    bullets: [
      "Sequenced lessons that build on each other",
      "Knowledge checks reinforce critical workflows",
      "Bookmark and revisit at any time",
    ],
    center_label: "You",
    roles: [
      { label: "Admins",           desc: "System config, permissions, integrations.",  top: "13%", left: "50%", delay: 0.20 },
      { label: "Sales",            desc: "Quotes, presentations, customer workflow.",   top: "39%", left: "85%", delay: 0.30 },
      { label: "Order Processing", desc: "Order entry, approvals, fulfillment.",        top: "80%", left: "72%", delay: 0.40 },
      { label: "Production",       desc: "Job tickets, scheduling, shop floor.",        top: "80%", left: "28%", delay: 0.50 },
      { label: "Ecommerce",        desc: "Stores, catalogs, online ordering.",          top: "39%", left: "15%", delay: 0.60 },
    ],
  },
  engine: {
    eyebrow: "The Active Learning Engine",
    heading: "Four stages. One continuous loop.",
    subheading: "Every cycle deepens retention - because doing beats watching.",
    stages: [
      { num: "01", icon: "route", title: "Curated Paths", desc: "Right courses, right order, for your role." },
      { num: "02", icon: "clock", title: "Paced Modules", desc: "Short, focused lessons that respect your time." },
      { num: "03", icon: "dumbbell", title: "Active Practice", desc: "Switch contexts to build true muscle memory." },
      { num: "04", icon: "bar-chart-3", title: "Progress Tracking", desc: "See what's done, what's next, what to revisit." },
    ],
    loop_label: "Repeat & Master",
  },
  method: {
    eyebrow: "The Antera Method",
    heading: "What you can expect.",
    subheading: "Six commitments that shape every module, every quiz, every video in the Academy.",
    cards: [
      { icon: "compass", title: "Clear Guidance", desc: "Step-by-step instructions with zero ambiguity.", accent: "border-l-sky-500" },
      { icon: "clapperboard", title: "Engaging Formats", desc: "Short, impactful lessons that respect your time.", accent: "border-l-sky-400" },
      { icon: "award", title: "Post Go-Live Value", desc: "Resources designed for long-term reference.", accent: "border-l-sky-600" },
      { icon: "dumbbell", title: "Hands-On Practice", desc: "Safe environments to test and learn by doing.", accent: "border-l-sky-500" },
      { icon: "file-text", title: "Real-World Scenarios", desc: "Examples tied to your operational reality.", accent: "border-l-sky-400" },
      { icon: "target", title: "Designed For Success", desc: "An overarching experience built to make you an expert.", accent: "border-l-sky-600" },
    ],
  },
  support: {
    eyebrow: "You're Never Alone",
    heading: "Hit a roadblock? Your support system is ready.",
    items: [
      { icon: "user-check", title: "Your Internal Trainer", desc: "Champion inside your organization who knows your workflows." },
      { icon: "route", title: "Your Project Manager", desc: "Guides your implementation roadmap from start to finish." },
      { icon: "headphones", title: "Antera Support Team", desc: "Industry experts a click away when it matters most." },
    ],
  },
  bottom_cta: {
    heading: "Ready to get started?",
    subheading: "Empower your team to master Antera and accelerate your onboarding.",
    primary_label: "Request A Demo",
    primary_href: "/contact",
    secondary_label: "Contact Us",
    secondary_href: "/contact",
  },
  related: {
    tag: "Antera Academy",
    title: "Training & Learning Resources",
    subtitle: "Tutorials, tips, and updates from the Antera Academy team.",
  },
};

export default function AcademyPage({ data }) {
  const src = data || {};
  const hero = { ...ACADEMY_DEFAULT_DATA.hero, ...(src.hero || {}) };
  const video = { ...ACADEMY_DEFAULT_DATA.video, ...(src.video || {}) };
  const goals = { ...ACADEMY_DEFAULT_DATA.goals, ...(src.goals || {}) };
  const paths = { ...ACADEMY_DEFAULT_DATA.paths, ...(src.paths || {}) };
  const engine = { ...ACADEMY_DEFAULT_DATA.engine, ...(src.engine || {}) };
  const method = { ...ACADEMY_DEFAULT_DATA.method, ...(src.method || {}) };
  const support = { ...ACADEMY_DEFAULT_DATA.support, ...(src.support || {}) };
  const bottomCta = { ...ACADEMY_DEFAULT_DATA.bottom_cta, ...(src.bottom_cta || {}) };
  const related = { ...ACADEMY_DEFAULT_DATA.related, ...(src.related || {}) };

  return (
    <div data-testid="academy-page" className="page-transition">

      {/* Hero - Split layout with animated stats */}
      <section className="pt-28 pb-0 lg:pt-36 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 mb-6">
                <Zap className="h-3.5 w-3.5 text-sky-600" />
                <span className="text-xs font-semibold text-sky-700 tracking-wide uppercase">{hero.eyebrow}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold tracking-tight text-slate-900 leading-[1.08]">
                {hero.title_lead}<br />
                <span className="text-sky-600">{hero.title_accent}</span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-slate-500 leading-relaxed max-w-lg">
                {hero.tagline}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {hero.primary_label ? (
                  <Link to={hero.primary_href || "/contact"}>
                    <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-12 text-sm font-semibold">
                      {hero.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                ) : null}
                {hero.secondary_label ? (
                  <a href={hero.secondary_href || "#method"}>
                    <Button variant="outline" className="rounded-full px-8 h-12 text-sm font-semibold border-slate-300 text-slate-700 hover:bg-slate-50">
                      {hero.secondary_label}
                    </Button>
                  </a>
                ) : null}
              </div>
            </motion.div>

            {/* Right - Stats + Visual */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <div className="bg-slate-900 rounded-2xl p-8 lg:p-10 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-sky-500/10 rounded-full blur-[60px]" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-sky-400/10 rounded-full blur-[50px]" />
                <div className="relative grid grid-cols-2 gap-6">
                  {(hero.stats || []).map((s, i) => (
                    <motion.div key={s.label || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                      className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5"
                    >
                      <Icon name={s.icon} className="h-5 w-5 text-sky-400 mb-3" />
                      <p className="text-3xl font-extrabold text-white">{s.value}</p>
                      <p className="text-xs text-slate-400 mt-1">{s.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Divider wave */}
        <div className="mt-16 lg:mt-24 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      </section>

      {/* Video Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
            <span className="text-xs font-semibold text-sky-600 tracking-widest uppercase">{video.eyebrow}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{video.heading}</h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-900"
          >
            <video
              controls
              playsInline
              muted
              preload="metadata"
              poster={video.poster}
              data-testid="academy-video"
              className="w-full h-auto aspect-video bg-slate-900"
            >
              <source src={video.src} type="video/mp4" />
            </video>
          </motion.div>
        </div>
      </section>

      {/* Learning Goals - Horizontal cards with large icons */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-xs font-semibold text-sky-600 tracking-widest uppercase">{goals.eyebrow}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{goals.heading}</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-0 border border-slate-200 rounded-2xl overflow-hidden">
            {(goals.items || []).map((g, i, arr) => (
              <motion.div key={g.title || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className={`p-8 lg:p-10 ${i < arr.length - 1 ? "md:border-r border-b md:border-b-0 border-slate-200" : ""}`}
              >
                <div className={`w-14 h-14 rounded-2xl ${g.color || "bg-sky-500"} flex items-center justify-center mb-5`}>
                  <Icon name={g.icon} className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{g.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{g.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curated Learning Paths - Hub and Spoke */}
      <section className="py-16 lg:py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-xs font-semibold text-sky-600 tracking-widest uppercase">{paths.eyebrow}</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                {paths.title_prefix} <span className="text-sky-600">{paths.title_accent}</span>
              </h2>
              <p className="mt-4 text-base text-slate-500 leading-relaxed">
                {paths.tagline}
              </p>
              <ul className="mt-8 space-y-4">
                {(paths.bullets || []).map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0" />
                    <span className="text-sm text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Hub and Spoke Diagram - Pentagon layout, 5 role cards evenly around "You".
                Each card is fixed-width so long descriptions wrap inside the card
                instead of expanding it and colliding with neighbors. */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="relative flex items-center justify-center py-8"
            >
              <div className="relative w-full max-w-[460px] aspect-square mx-auto">
                {/* Connector lines - viewBox 460 matches container aspect */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 460 460" preserveAspectRatio="none">
                  <line x1="230" y1="230" x2="230" y2="60"  stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />
                  <line x1="230" y1="230" x2="391" y2="176" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />
                  <line x1="230" y1="230" x2="329" y2="367" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />
                  <line x1="230" y1="230" x2="131" y2="367" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />
                  <line x1="230" y1="230" x2="69"  y2="176" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="6 4" />
                </svg>

                {/* Center circle - "You" */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-slate-900 flex items-center justify-center shadow-xl z-10">
                  <span className="text-xl font-bold text-white">{paths.center_label}</span>
                </div>

                {/* All 5 satellite cards share identical styling. */}
                {(paths.roles || []).map((c) => (
                  <motion.div
                    key={c.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: c.delay }}
                    style={{ top: c.top, left: c.left }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 w-[130px] bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-md text-center hover:shadow-lg transition-shadow z-20"
                  >
                    <p className="text-sm font-bold text-slate-900 leading-tight">{c.label}</p>
                    <p className="text-[10px] text-slate-400 mt-1 leading-snug">{c.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Active Learning Engine - Numbered timeline */}
      <section className="py-16 lg:py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-[10%] w-64 h-64 bg-sky-500/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 right-[10%] w-80 h-80 bg-sky-400/5 rounded-full blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-sky-400 tracking-widest uppercase">{engine.eyebrow}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {engine.heading}
            </h2>
            <p className="mt-3 text-base text-slate-400 max-w-lg mx-auto">{engine.subheading}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(engine.stages || []).map((s, i, arr) => (
              <motion.div key={s.num || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="group relative"
              >
                <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl p-6 lg:p-7 hover:border-sky-500/30 transition-all duration-300 h-full">
                  <div className="text-5xl font-extrabold text-sky-500/10 absolute top-4 right-5">{s.num}</div>
                  <div className="w-11 h-11 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mb-5">
                    <Icon name={s.icon} className="h-5 w-5 text-sky-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                    <ArrowRight className="h-5 w-5 text-sky-500/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Loop arrow */}
          {engine.loop_label ? (
            <div className="flex justify-center mt-8">
              <div className="flex items-center gap-2 text-sky-500/40">
                <div className="h-px w-16 bg-sky-500/20" />
                <span className="text-xs font-medium tracking-wider uppercase">{engine.loop_label}</span>
                <div className="h-px w-16 bg-sky-500/20" />
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* The Antera Method - Bento grid */}
      <section id="method" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold text-sky-600 tracking-widest uppercase">{method.eyebrow}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{method.heading}</h2>
            <p className="mt-3 text-base text-slate-500 max-w-lg mx-auto">{method.subheading}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(method.cards || []).map((c, i) => (
              <motion.div key={c.title || i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className={`bg-white border border-slate-200 border-l-4 ${c.accent || "border-l-sky-500"} rounded-xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                    <Icon name={c.icon} className="h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{c.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Support System */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold text-sky-600 tracking-widest uppercase">{support.eyebrow}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {support.heading}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {(support.items || []).map((s, i) => (
              <motion.div key={s.title || i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white border border-slate-200 rounded-2xl p-7 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center mx-auto mb-5">
                  <Icon name={s.icon} className="h-7 w-7 text-sky-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-sky-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-[20%] w-96 h-96 bg-sky-400/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-[20%] w-72 h-72 bg-white/5 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6">
            <Play className="h-8 w-8 text-white fill-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">{bottomCta.heading}</h2>
          <p className="mt-4 text-base text-sky-100 max-w-md mx-auto">{bottomCta.subheading}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {bottomCta.primary_label ? (
              <Link to={bottomCta.primary_href || "/contact"}>
                <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
                  {bottomCta.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            ) : null}
            {bottomCta.secondary_label ? (
              <Link to={bottomCta.secondary_href || "/contact"}>
                <Button variant="outline" className="rounded-full px-8 h-12 text-sm font-semibold border-white/30 text-white hover:bg-sky-700 bg-transparent">
                  {bottomCta.secondary_label}
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      <RelatedContentCarousel
        tag={related.tag}
        title={related.title}
        subtitle={related.subtitle}
        testId="academy-related"
      />
    </div>
  );
}
